export const LISSEN_IOS_APP_STORE_URL =
  "https://apps.apple.com/gb/app/lissen/id6444801516";

export const LISSEN_ANDROID_PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.lissen.app&hl=en_GB";

export type BranchLinkData = Record<
  string,
  string | number | boolean | null | undefined | string[]
>;

export type BranchCreateUrlMetadata = {
  channel?: string;
  feature?: string;
  campaign?: string;
  tags?: string[];
};

type BranchCreateUrlBaseInput = BranchCreateUrlMetadata & {
  data: BranchLinkData;
  iosStoreUrl?: string;
  androidStoreUrl?: string;
  desktopDeepview?: string;
};

export type BranchCreateUrlHttpInput = BranchCreateUrlBaseInput & {
  branchKey: string;
  createUrl?: never;
};

export type BranchCreateUrlWithAdapterInput = BranchCreateUrlBaseInput & {
  branchKey?: never;
  createUrl: (
    data: BranchLinkData,
    metadata: BranchCreateUrlMetadata,
  ) => Promise<string | null>;
};

export type BranchCreateUrlInput =
  | BranchCreateUrlHttpInput
  | BranchCreateUrlWithAdapterInput;

export type BranchCreateUrlResponse = {
  url: string;
};

export class BranchApiError extends Error {
  readonly status?: number;
  readonly bodySnippet?: string;

  constructor(
    message: string,
    options?: { status?: number; bodySnippet?: string; cause?: unknown },
  ) {
    super(message, options);
    this.name = "BranchApiError";
    this.status = options?.status;
    this.bodySnippet = options?.bodySnippet;
  }
}

export function isTransientBranchError(err: unknown): boolean {
  if (err instanceof BranchApiError) {
    if (err.status === 429) return true;
    if (err.status !== undefined && err.status >= 500) return true;
    if (err.status === 400 || err.status === 401 || err.status === 403) {
      return false;
    }
    if (err.bodySnippet?.trimStart().startsWith("<")) return true;
    return true;
  }

  if (err instanceof SyntaxError) return true;

  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    return (
      msg.includes("429") ||
      msg.includes("rate limit") ||
      msg.includes("500") ||
      msg.includes("502") ||
      msg.includes("503") ||
      msg.includes("504") ||
      msg.includes("timeout") ||
      msg.includes("econnreset") ||
      msg.includes("econnrefused") ||
      msg.includes("fetch failed") ||
      msg.includes("network")
    );
  }

  return false;
}

export function removeUnsetBranchValues(data: BranchLinkData): BranchLinkData {
  return Object.fromEntries(
    Object.entries(data).filter(([, value]) => value !== undefined),
  );
}

function parseBranchResponseBody(
  responseText: string,
  status: number,
): Partial<BranchCreateUrlResponse> & {
  error?: { code?: number; message?: string };
} {
  try {
    return JSON.parse(responseText) as Partial<BranchCreateUrlResponse> & {
      error?: { code?: number; message?: string };
    };
  } catch (error) {
    throw new BranchApiError(
      `Branch API returned non-JSON response (${status})`,
      {
        status,
        bodySnippet: responseText.slice(0, 200),
        cause: error,
      },
    );
  }
}

export async function createBranchUrl({
  androidStoreUrl,
  branchKey,
  campaign,
  channel,
  createUrl,
  data,
  desktopDeepview,
  feature,
  iosStoreUrl,
  tags,
}: BranchCreateUrlInput): Promise<string> {
  const linkData = removeUnsetBranchValues({
    ...data,
    $ios_url: iosStoreUrl ?? LISSEN_IOS_APP_STORE_URL,
    $ipad_url: iosStoreUrl ?? LISSEN_IOS_APP_STORE_URL,
    $android_url: androidStoreUrl ?? LISSEN_ANDROID_PLAY_STORE_URL,
    $ios_deepview: false,
    $android_deepview: false,
    $ios_passive_deepview: false,
    $android_passive_deepview: false,
    $mobile_web_only: false,
    $web_only: false,
    $desktop_web_only: false,
    $desktop_deepview: desktopDeepview ?? "default_template",
    $desktop_url: undefined,
    $fallback_url: undefined,
  });

  if (createUrl) {
    const url = await createUrl(linkData, { campaign, channel, feature, tags });
    if (!url) {
      throw new BranchApiError(
        "Branch link creation response did not include a URL",
      );
    }

    return url;
  }

  let response: Response;
  try {
    response = await fetch("https://api2.branch.io/v1/url", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(
        removeUnsetBranchValues({
          branch_key: branchKey,
          campaign,
          channel,
          feature,
          tags,
          data: JSON.stringify(removeUnsetBranchValues(linkData)),
        }),
      ),
    });
  } catch (error) {
    throw new BranchApiError("Branch link creation request failed", {
      cause: error,
    });
  }

  const responseText = await response.text();
  const body = parseBranchResponseBody(responseText, response.status);

  if (!response.ok) {
    const branchMessage = body.error?.message;
    throw new BranchApiError(
      branchMessage
        ? `Branch link creation failed with ${response.status}: ${branchMessage}`
        : `Branch link creation failed with ${response.status}`,
      {
        status: response.status,
        bodySnippet: responseText.slice(0, 200),
      },
    );
  }

  if (!body.url) {
    throw new BranchApiError(
      "Branch link creation response did not include a URL",
      {
        status: response.status,
        bodySnippet: responseText.slice(0, 200),
      },
    );
  }

  return body.url;
}
